<template>
  <div>
    <b-modal
      v-model="show_"
      :no-close-on-backdrop="true"
      :no-close-on-esc="true"
      dialog-class="pb-0"
      hide-footer
    >
      <template #modal-title>
        {{ $t(`${tt}.modal.title`) }}
      </template>

      <h6 class="card-title mb-0">
        {{ $t(`${tt}.modal.description`) }}
      </h6>
      <div class="text-center">
        <button
          type="button"
          :disabled="uploadProcess"
          class="file-upload btn btn-light btn-xl mt-4"
        >
          <i
            class="mr-1 align-middle"
            :class="[uploadProcess ? 'ri-loader-2-line spinner' : 'ri-file-upload-line']"
          />
          {{ $t(`${tt}.modal.btn_upload`) }}
          <input
            ref="file"
            type="file"
            accept="video/*"
            @change="fileUpload"
          >
        </button>
      </div>
      <blockquote
        v-if="!gallery.loading && !gallery.items.length"
        class="blockquote text-center py-2 mt-3 mb-0"
      >
        <div class="mb-3">
          <img
            src="@/static/gallery.png"
            width="50%"
          >
        </div>
        <p
          class="text-muted mb-5"
          v-html="$t(`${tt}.modal.empty_video_description`)"
        />
      </blockquote>

      <div v-else>
        <div class="pt-4 pb-3">
          <div
            v-for="item of gallery.items"
            :key="`video-${item.shortHash}`"
          >
            <button
              :disabled="!item.mimeTypeCheck || !item.thumbnail"
              class="list-group-item list-group-item-action d-flex align-items-center mb-2"
              type="button"
              @click.prevent="onSelect(item)"
            >
              <i class="icon-file-pdf mr-3" />
              <span class="text-truncate">
                {{ item.name }}
              </span>
              <span class="text-muted ml-2 font-size-sm text-nowrap">
                {{ item.createdTime | dateTimeHuman }}
              </span>
              <div class="flex-grow-1 d-flex justify-content-end ml-3">
                <span class="badge badge-light ml-3">
                  {{ item.size | formatBytes }}
                </span>
              </div>
            </button>
          </div>
        </div>
        <div
          v-if="gallery.totalRows > gallery.perPage"
          class="d-flex justify-content-end"
        >
          <b-pagination
            v-model="gallery.currentPage"
            :hide-goto-end-buttons="true"
            :per-page="gallery.perPage"
            :total-rows="gallery.totalRows"
          />
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import catchDispatch from "@/utils/catchDispatch";
import axios from "axios";
import moment from 'moment';

const gallery = () => ({
  loading: false,
  items: [],
  perPage: 8,
  totalRows: 0,
  currentPage: 1,
});

export function getMoment(timestamp) {
  if (timestamp instanceof moment) {
    return timestamp;
  } else if (/^[0-9]+$/.test(timestamp)) {
    return moment.utc(timestamp, 'X');
  } else {
    return moment.utc(timestamp);
  }
}

export default {
  filters: {
    dateTimeHuman(timestamp) {
      if (!timestamp) return '';
      return getMoment(timestamp).format('DD.MM.YYYY HH:mm');
    },
    formatBytes(bytes) {
      if (bytes === 0) return '0 Bytes';

      const k = 1024;
      const decimals = 0;
      const dm = decimals < 0 ? 0 : decimals;
      const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

      const i = Math.floor(Math.log(bytes) / Math.log(k));

      return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    },
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    tt: 'room.footer.attach',
    restriction: 'viber',
    uploadProcess: false,
    gallery: gallery(),
  }),
  computed: {
    show_: {
      get() {
        return this.show;
      },
      set(value) {
        this.$emit('update:show', value);
      },
    },
  },
  watch: {
    show_(show) {
      show && this.onShow();
    },
    'gallery.currentPage'() {
      this.fetch();
    },
  },
  mounted() {
    this.show_ && this.onShow();
  },
  methods: {
    onShow() {
      Object.assign(this.gallery, gallery());
      this.fetch();
    },
    fileUpload(e) {
      e.preventDefault();

      if (this.uploadProcess) return;
      this.uploadProcess = true;

      const fileForm = new FormData();
      fileForm.append('data', e.target.files[0]);
      fileForm.append('meta', JSON.stringify({public: true}));

      axios
        .post(`/api-gateway/api/v1/drive/upload/files/messages`, fileForm)
        .then(({data}) => this.onSelect(data))
        .catch(e => {
          this.$refs.file.value = '';
          catchDispatch.call(this, e);
        })
        .finally(() => {
          this.uploadProcess = false;
        });
    },
    fetch() {
      if (this.gallery.loading) return;
      this.gallery.loading = true;

      const mimeType = 'video/';

      axios
        .get(`/drive/api/v1/files/messages`, {
          params: {
            skip: (this.gallery.currentPage - 1) * this.gallery.perPage,
            limit: this.gallery.perPage,
            order_by: 'created_at desc',
          },
        })
        .then(({data}) => ({
          items: data.content || [],
          totalRows: data.stats.count,
        }))
        .then(({items, totalRows}) => {
          this.gallery.items = items.map(item => ({
            ...item,
            mimeTypeCheck: item.mimeType.startsWith(mimeType),
          }));
          this.gallery.totalRows = totalRows;
        })
        .catch(e => catchDispatch(this, e))
        .finally(() => this.gallery.loading = false);
    },
    makeUrl(item) {
      const {protocol} = window.location;
      let {host} = window.location;
      const {category, shortHash, name} = item;
      if (process.env.NODE_ENV !== 'production' || window.location.host.startsWith('dev-chat.')) {
        host = 'staging.messaggio.com';
      }
      return `${protocol}//${host}/drive/api/v1/public/files/${category}/${shortHash}/${name}?alt=media`;
    },
    onSelect(item) {
      this.show_ = false;
      const payload = {
        type: 'video',
        attachment: {
          url: this.makeUrl(item),
          url_thumbnail: this.makeUrl(item.thumbnail),
          duration: item.videoLength,
          size: item.size,
        },
      };
      this.$emit('input', payload);
    },
    onReset() {
      if (this.$refs.file) this.$refs.file.value = '';
      this.$emit('input', undefined);
    },
  },
};
</script>

<style scoped>
.file-upload {
  position: relative;
  cursor: pointer;
}

.file-upload input[type=file] {
  -webkit-appearance: none;
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  opacity: 0;
  width: 100%;
  height: 100%;
}

.card-img-actions {
  position: relative;
}

.card-img-actions:hover .card-img-actions-overlay,
.card-img-actions:focus .card-img-actions-overlay {
  opacity: 1;
  visibility: visible;
}

.card-img-actions-overlay {
  display: -ms-flexbox;
  display: flex;
  -ms-flex-align: center;
  align-items: center;
  -ms-flex-pack: center;
  justify-content: center;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(0, 0, 0, 0.75);
  color: #fff;
  opacity: 0;
  visibility: hidden;
  transition: all ease-in-out 0.15s;
}

@media screen and (prefers-reduced-motion: reduce) {
  .card-img-actions-overlay {
    transition: none;
  }
}

.border-2 {
  border: 2px solid;
}
</style>
